import { computed, onMounted, ref } from "vue";
import { useToast } from "vue-toastification";
import { getClassMessages, isClassSession } from "@/utils/api";
import { getMessage } from "@/utils/mobileapi";
import { formatTime } from "@/utils/utils";
import { safeList } from "@/utils/http";
const readUserData = () => {
  try {
    return JSON.parse(localStorage.getItem("userData") || "null");
  } catch (error) {
    console.error(error);
    return null;
  }
};
const toTimestamp = (message = {}) => {
  const value = message.created_at || message.createdAt || message.updatedAt || message.sendTime || message.time || message.create_time;
  if (!value) return 0;
  if (typeof value === "number") return value > 100000000000 ? Math.floor(value / 1000) : value;
  if (/^\d+$/.test(String(value))) {
    const number = Number(value);
    return number > 100000000000 ? Math.floor(number / 1000) : number;
  }
  const parsed = Date.parse(String(value).replace(/-/g, "/"));
  return Number.isNaN(parsed) ? 0 : Math.floor(parsed / 1000);
};
const looksLikeHtml = (value = "") => /<\/?[a-z][\s\S]*>/i.test(String(value));
const decodeEntities = (value = "") => {
  if (typeof window === "undefined") return String(value || "");
  const textarea = document.createElement("textarea");
  textarea.innerHTML = String(value || "");
  return textarea.value;
};
const stripHtml = (value = "") => {
  const raw = String(value || "");
  if (!raw) return "";
  const withBreaks = raw.replace(/<\s*br\s*\/?>/gi, "\n").replace(/<\/\s*p\s*>/gi, "\n").replace(/<\/\s*div\s*>/gi, "\n").replace(/<\/\s*li\s*>/gi, "\n").replace(/<\/\s*h[1-6]\s*>/gi, "\n").replace(/<\/\s*tr\s*>/gi, "\n");
  const noTags = withBreaks.replace(/<[^>]+>/g, " ");
  return decodeEntities(noTags).replace(/\u00a0/g, " ").replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").replace(/[ \t]{2,}/g, " ").trim();
};
const sanitizeHtml = (value = "") => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (typeof window === "undefined") return raw;
  const template = document.createElement("template");
  template.innerHTML = raw;
  const allowed = new Set(["P", "BR", "DIV", "SPAN", "STRONG", "B", "EM", "I", "U", "S", "H1", "H2", "H3", "H4", "H5", "H6", "UL", "OL", "LI", "TABLE", "THEAD", "TBODY", "TFOOT", "TR", "TH", "TD", "BLOCKQUOTE", "PRE", "CODE", "HR", "A", "IMG", "SECTION", "ARTICLE"]);
  const walk = node => {
    const children = Array.from(node.childNodes);
    children.forEach(child => {
      if (child.nodeType === Node.ELEMENT_NODE) {
        if (!allowed.has(child.tagName)) {
          const parent = child.parentNode;
          while (child.firstChild) parent.insertBefore(child.firstChild, child);
          parent.removeChild(child);
          return;
        }
        Array.from(child.attributes).forEach(attr => {
          const name = attr.name.toLowerCase();
          const val = attr.value || "";
          const safeAttr = name === "href" || name === "src" || name === "alt" || name === "title" || name === "colspan" || name === "rowspan" || name === "class";
          if (!safeAttr || /^on/i.test(name) || /javascript:/i.test(val)) {
            child.removeAttribute(attr.name);
          }
        });
        if (child.tagName === "A") {
          const href = child.getAttribute("href") || "";
          if (!/^(https?:|mailto:|tel:|#|\/)/i.test(href)) {
            child.removeAttribute("href");
          } else {
            child.setAttribute("target", "_blank");
            child.setAttribute("rel", "noopener noreferrer");
          }
        }
        walk(child);
      } else if (child.nodeType === Node.COMMENT_NODE) {
        child.parentNode.removeChild(child);
      }
    });
  };
  walk(template.content);
  return template.innerHTML;
};
const plainToHtml = (value = "") => {
  const text = decodeEntities(String(value || "")).trim();
  if (!text) return "";
  return text.split(/\n{2,}/).map(block => {
    const lines = block.split(/\n/).map(line => line.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")).join("<br>");
    return `<p>${lines}</p>`;
  }).join("");
};
export default {
  setup() {
    const toast = useToast();
    const messages = ref([]);
    const loading = ref(false);
    const currentPage = ref(1);
    const pageSize = ref(10);
    const totalItems = ref(0);
    const detailVisible = ref(false);
    const activeMessage = ref(null);
    const hasNextPage = computed(() => {
      if (totalItems.value) return currentPage.value * pageSize.value < totalItems.value;
      return messages.value.length >= pageSize.value;
    });
    const messageRawContent = message => message?.content || message?.body || message?.desc || message?.summary || "";
    const detailPlain = computed(() => stripHtml(messageRawContent(activeMessage.value)));
    const detailHtml = computed(() => {
      const raw = messageRawContent(activeMessage.value);
      if (!raw) return "";
      if (looksLikeHtml(raw)) return sanitizeHtml(raw);
      return plainToHtml(raw);
    });
    const previewText = message => {
      const text = stripHtml(messageRawContent(message));
      if (!text) return "暂无内容";
      return text.length > 96 ? `${text.slice(0, 96)}…` : text;
    };
    const goLogin = () => {
      window.location.hash = "#/login";
    };
    const ensureLoggedIn = () => {
      const loggedIn = localStorage.getItem("loggedIn");
      const userData = readUserData();
      const valid = loggedIn === "true" && userData && (isClassSession(userData) || userData.oauth_token && userData.oauth_token_secret);
      if (!valid) {
        toast.error("未登录或 token 失效，请重新登录", {
          position: "top-center"
        });
        goLogin();
        return null;
      }
      return userData;
    };
    const normalizeClassMessage = message => ({
      ...message,
      message_id: message.id,
      title: message.title || message.name,
      content: message.content || message.body || message.desc,
      created_at: toTimestamp(message)
    });
    const fetchMessages = async () => {
      const userData = ensureLoggedIn();
      if (!userData) return;
      loading.value = true;
      try {
        if (isClassSession(userData)) {
          const response = await getClassMessages(userData, currentPage.value, 0, pageSize.value);
          const list = safeList(response).map(normalizeClassMessage);
          messages.value = list;
          totalItems.value = response?.total_count || list.length;
          return;
        }
        const response = await getMessage(userData.oauth_token, userData.oauth_token_secret, currentPage.value, "1");
        const list = safeList(response);
        messages.value = list.map(message => ({
          ...message,
          created_at: toTimestamp(message)
        }));
        totalItems.value = response?.total_count || response?.content?.total_count || list.length;
      } catch (error) {
        toast.error("获取通知失败，请检查网络连接。", {
          position: "top-center"
        });
        console.error(error);
      } finally {
        loading.value = false;
      }
    };
    const openMessage = message => {
      activeMessage.value = message;
      detailVisible.value = true;
    };
    const formatMessageTime = message => {
      const timestamp = toTimestamp(message);
      return timestamp ? formatTime(timestamp) : "—";
    };
    const dateParts = message => {
      const timestamp = toTimestamp(message);
      if (!timestamp) return {
        day: "--",
        month: "未知"
      };
      const date = new Date(timestamp * 1000);
      return {
        day: String(date.getDate()).padStart(2, "0"),
        month: `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, "0")}`
      };
    };
    const prevPage = () => {
      if (currentPage.value > 1) {
        currentPage.value -= 1;
        fetchMessages();
      }
    };
    const nextPage = () => {
      if (!hasNextPage.value) return;
      currentPage.value += 1;
      fetchMessages();
    };
    onMounted(fetchMessages);
    return {
      messages,
      loading,
      currentPage,
      hasNextPage,
      prevPage,
      nextPage,
      detailVisible,
      activeMessage,
      openMessage,
      formatMessageTime,
      dateParts,
      previewText,
      detailHtml,
      detailPlain
    };
  }
};